import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Check, Copy, MoreVertical, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DataTable } from '@/components/super-admin/data-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RedactedEmail } from '@/components/redacted-email';
import { userDisplayName, userNameOrNull } from '@/lib/user-display-name';

export interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  // Nullable in the DB, so nullable here: WorkOS-provisioned rows can lack one.
  email: string | null;
  channelCount: number;
  createdAt: string;
}

interface UsersTableProps {
  users: User[];
  isLoading: boolean;
  isDeleting: boolean;
  onDelete: (ids: string[]) => Promise<void>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /** Changes whenever the displayed page/search/sort changes. */
  resetToken: string;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={`Copy ${label}`}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label} copied`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error(`Failed to copy ${label}`);
        }
      }}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-emerald-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

export function UsersTable({
  users,
  isLoading,
  isDeleting,
  onDelete,
  sorting,
  onSortingChange,
  resetToken,
}: UsersTableProps) {
  const navigate = useNavigate();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [confirmIds, setConfirmIds] = useState<string[] | null>(null);

  // Selection and any open confirmation refer to rows that may no longer be
  // displayed once page/search/sort changes. Reset on the token rather than
  // remounting: the sort button the operator just clicked lives in this subtree,
  // and a remount would drop focus.
  useEffect(() => {
    setRowSelection({});
    setConfirmIds(null);
  }, [resetToken]);

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <span
            onClick={(e) => e.stopPropagation()}
            className="inline-flex"
          >
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && 'indeterminate')
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          </span>
        ),
        cell: ({ row }) => (
          <span
            onClick={(e) => e.stopPropagation()}
            className="inline-flex"
          >
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
            />
          </span>
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: 'name',
        accessorFn: (u) => userDisplayName(u),
        header: 'Name',
        enableSorting: true,
        meta: { sortLabel: 'first name' },
        cell: ({ row }) => {
          // No name → fall back to the email, masked via RedactedEmail like
          // the Email column (screen-share-safe convention). The copy button
          // copies the raw value, matching the Email column's behavior.
          const name = userNameOrNull(row.original);
          const email = row.original.email;
          if (!name && !email) return '—';
          return (
            <div className="flex items-center gap-1.5">
              {name ? <span>{name}</span> : <RedactedEmail email={email!} />}
              <CopyButton value={name ?? email!} label="Name" />
            </div>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
        meta: { sortLabel: 'email' },
        cell: ({ row }) =>
          row.original.email ? (
            <div className="flex items-center gap-1.5">
              <RedactedEmail email={row.original.email} />
              <CopyButton value={row.original.email} label="Email" />
            </div>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'channelCount',
        header: () => <div className="text-center">Channels</div>,
        // Server-side sort: the count is two hops from User, so the backend
        // orders it in SQL (see super-admin.service.ts).
        enableSorting: true,
        meta: { sortLabel: 'channel count' },
        cell: ({ row }) => (
          <div className="text-center">{row.original.channelCount}</div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Joined',
        enableSorting: true,
        meta: { sortLabel: 'joined date' },
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'actions',
        enableSorting: false,
        enableHiding: false,
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div
            className="text-right"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Actions for ${row.original.email ?? userDisplayName(row.original)}`}
                  />
                }
              >
                <MoreVertical className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem
                  onClick={() =>
                    navigate(`/super-admin/users/${row.original.id}`)
                  }
                >
                  View details
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setConfirmIds([row.original.id])}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete user
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [navigate],
  );

  const table = useReactTable({
    data: users,
    columns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    // The server has no unsorted state — a request with no sort parameters still
    // returns createdAt desc — so removal would show sorted data with no aria-sort.
    enableSortingRemoval: false,
    manualSorting: true,
    state: { rowSelection, sorting },
    onRowSelectionChange: setRowSelection,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
  });

  const selectedIds = table
    .getSelectedRowModel()
    .rows.map((r) => r.original.id);

  const runDelete = async (ids: string[]) => {
    if (ids.length === 0) {
      setConfirmIds(null);
      return;
    }
    await onDelete(ids);
    setRowSelection({});
    setConfirmIds(null);
  };

  return (
    <>
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
          <span className="text-sm">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRowSelection({})}
              disabled={isDeleting}
            >
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={isDeleting}
              onClick={() => setConfirmIds(selectedIds)}
            >
              <Trash2 className="h-4 w-4" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      <DataTable
        table={table}
        isLoading={isLoading}
        emptyState="No users."
        rowClassName={(row, index) =>
          row.getIsSelected() ? 'bg-muted' : index % 2 === 1 ? 'bg-muted/50' : ''
        }
      />

      <AlertDialog
        open={confirmIds !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmIds(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {confirmIds?.length === 1 ? 'user' : `${confirmIds?.length ?? 0} users`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected user
              {confirmIds && confirmIds.length === 1 ? '' : 's'} from the
              database, plus their workspaces, Stripe customer, GHL contact,
              and WorkOS account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting || !confirmIds || confirmIds.length === 0}
              onClick={(e) => {
                e.preventDefault();
                if (confirmIds && confirmIds.length > 0) {
                  void runDelete(confirmIds);
                }
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
